/**
 * Maintenance Modal Component
 * Modal for recording vehicle maintenance
 */

import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { useRecordMaintenance, useVehicle } from '../../../hooks/useVehicles';
import { recordMaintenanceSchema, maintenanceTypes } from '../../../schemas/vehicleSchema';
import type { RecordMaintenanceFormData } from '../../../schemas/vehicleSchema';

interface MaintenanceModalProps {
  vehicleId: string;
  open: boolean;
  onClose: () => void;
}

export function MaintenanceModal({ vehicleId, open, onClose }: MaintenanceModalProps) {
  const { mutateAsync: recordMaintenance, isPending } = useRecordMaintenance();
  const { data: vehicle } = useVehicle(vehicleId);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState<RecordMaintenanceFormData>({
    type: 'scheduled',
    description: '',
    odometer: 0,
    cost: undefined,
    performedBy: '',
  });

  useEffect(() => {
    if (open && vehicle) {
      setFormData({
        type: 'scheduled',
        description: '',
        odometer: vehicle.odometer,
        cost: undefined,
        performedBy: '',
      });
      setErrors({});
    }
  }, [open, vehicle]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Convert empty cost to undefined
    const dataToValidate = {
      ...formData,
      cost: formData.cost === '' || formData.cost === undefined ? undefined : formData.cost,
    };

    const result = recordMaintenanceSchema.safeParse(dataToValidate);
    if (!result.success) {
      const newErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) {
          newErrors[String(err.path[0])] = err.message;
        }
      });
      setErrors(newErrors);
      return;
    }

    // Filter out empty string from cost before sending to API
    const requestData = {
      ...result.data,
      cost: result.data.cost === '' ? undefined : result.data.cost,
    };

    try {
      await recordMaintenance({ vehicleId, data: requestData });
      onClose();
    } catch (error) {
      setErrors({
        submit: error instanceof Error ? error.message : 'Failed to record maintenance',
      });
    }
  };

  const updateField = (
    field: keyof RecordMaintenanceFormData,
    value: string | number | undefined
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Record Maintenance</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Maintenance Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Maintenance Type <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.type}
              onChange={(e) => updateField('type', e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              {maintenanceTypes.map((type) => (
                <option key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => updateField('description', e.target.value)}
              rows={3}
              className={`mt-1 block w-full rounded-md border ${
                errors.description ? 'border-red-500' : 'border-gray-300'
              } px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500`}
              placeholder="Oil change, tire rotation, etc."
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-500">{errors.description}</p>
            )}
          </div>

          {/* Odometer & Cost */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Odometer Reading <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={formData.odometer}
                onChange={(e) => updateField('odometer', parseInt(e.target.value))}
                className={`mt-1 block w-full rounded-md border ${
                  errors.odometer ? 'border-red-500' : 'border-gray-300'
                } px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500`}
              />
              {errors.odometer && <p className="mt-1 text-sm text-red-500">{errors.odometer}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Cost ($)</label>
              <input
                type="number"
                step="0.01"
                value={formData.cost || ''}
                onChange={(e) => updateField('cost', e.target.value ? parseFloat(e.target.value) : undefined)}
                className={`mt-1 block w-full rounded-md border ${
                  errors.cost ? 'border-red-500' : 'border-gray-300'
                } px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500`}
                placeholder="Optional"
              />
              {errors.cost && <p className="mt-1 text-sm text-red-500">{errors.cost}</p>}
            </div>
          </div>

          {/* Performed By */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Performed By</label>
            <input
              type="text"
              value={formData.performedBy}
              onChange={(e) => updateField('performedBy', e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              placeholder="Shop name or technician"
            />
          </div>

          {/* Maintenance History */}
          {vehicle?.maintenanceRecords && vehicle.maintenanceRecords.length > 0 && (
            <div className="rounded-lg border border-gray-200 p-3">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Recent Maintenance</h4>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {vehicle.maintenanceRecords.slice(0, 5).map((record) => (
                  <div key={record.id} className="text-sm">
                    <div className="flex justify-between">
                      <span className="font-medium capitalize">{record.type}</span>
                      <span className="text-gray-500">
                        {new Date(record.date).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="text-gray-600">{record.description}</div>
                    <div className="text-xs text-gray-500">
                      {record.odometer.toLocaleString()} mi
                      {record.cost && ` • $${record.cost.toFixed(2)}`}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {errors.submit && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{errors.submit}</div>
          )}

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Recording...
                </>
              ) : (
                'Record Maintenance'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
